/**
 * True when `assetsHost` points at a local dev assets server. On local dev the
 * campaign/tournament template assets aren't served locally, so we keep the
 * full resource URLs the backend already provides (see `stripResourcesHost`)
 * instead of rewriting relative paths against this host.
 */
export const isLocalAssetsHost = (assetsHost: string): boolean =>
  assetsHost.includes('localhost') || assetsHost.includes('127.0.0.1');

/**
 * Prepends `assetsHost` to relative `resources.*` URLs in an item.
 *
 * When the backend honours `stripResourcesHost=true` it returns relative
 * paths (e.g. `/qa/templates/bg.webp?v=1`); the lobby is then responsible
 * for assembling the full URL. Older backends still return absolute URLs
 * — those pass through untouched, so the rollout is zero-downtime.
 */
export const buildResourceUrls = <T extends { resources?: Record<string, unknown> }>(item: T, assetsHost: string): T => {
  const resources = item.resources;
  if (!resources || !assetsHost) return item;

  const host = assetsHost.replace(/\/$/, '');
  // Only relative paths (starting with `/`) are rewritten. Absolute URLs
  // (`http(s)://...`) and non-path strings (CSS colors like `#111113`,
  // hex values, etc.) pass through untouched.
  const buildUrl = (value: unknown): unknown =>
    typeof value === 'string' && value.startsWith('/') ? `${host}${value}` : value;

  const resolved = Object.fromEntries(Object.entries(resources).map(([key, value]) => [key, buildUrl(value)]));

  return { ...item, resources: resolved };
};
